'use client';

import type React from 'react';
import { CableIcon, Location01Icon } from '@terra/ui-icons';
import {
  formatDefaultPayload,
  renderCodeResult,
  renderJsonResult,
  renderMarkdownResult,
  renderMcpBloodhoundResult,
  renderPlainTextResult,
} from './tools';
import {
  getMcpToolDisplayName,
  isMcpToolName,
  normalizeToolName,
} from '@terra/shared-chat-trace/trace-view/utils/tool-config.helpers';
import {
  renderDefaultEditContent,
  renderDefaultEditModal,
  renderMarkdownContent,
} from './renderers';
import {
  renderEndpointResult,
  renderEndpointsListResult,
} from './tools/endpoints';
import {
  renderWebPageResult,
  renderWebPagesListResult,
} from './tools/web-pages';
import { buildExecutableToolConfigs } from './tool-config-executable';
import { buildIntelligenceToolConfigs } from './tool-config-intelligence';
import { buildDomainAndMemoryToolConfigs } from './tool-config-domain-and-memory';
import { buildAgentsAndMcpToolConfigs } from './tool-config-agents-and-mcp';
import type { OutputRenderContext, ToolConfig } from './tool-config-types';

export type {
  ToolConfig,
  ToolCategory,
  CardType,
  HITLStatus,
  ToolIconSize,
  HITLContentContext,
  EditModalProps,
  EditContentProps,
  StreamingContentProps,
  OutputRenderContext,
  MetadataItem,
  CustomAction,
  CardRenderContext,
  ContentViewContext,
  ParsedToolOutput,
  HarToolType,
  HITLConfig,
} from './tool-config-types';
export type { HttpToolInput, HttpToolOutput } from './extractors';

let toolDisplayConfigCache: Record<string, ToolConfig> | null = null;
const getToolDisplayConfig = (): Record<string, ToolConfig> => {
  if (toolDisplayConfigCache) {
    return toolDisplayConfigCache;
  }

  toolDisplayConfigCache = {
    ...buildExecutableToolConfigs(),
    ...buildIntelligenceToolConfigs(),
    ...buildDomainAndMemoryToolConfigs(),
    ...buildAgentsAndMcpToolConfigs(),
  };

  return toolDisplayConfigCache;
};

let contentTypeRenderersCache: Record<
  string,
  (ctx: OutputRenderContext) => React.ReactNode
> | null = null;
const getContentTypeRenderers = (): Record<
  string,
  (ctx: OutputRenderContext) => React.ReactNode
> => {
  if (contentTypeRenderersCache) {
    return contentTypeRenderersCache;
  }

  contentTypeRenderersCache = {
    json: renderJsonResult,
    markdown: renderMarkdownResult,
    'plain-text': renderPlainTextResult,
    endpoint: renderEndpointResult,
    'endpoints-list': renderEndpointsListResult,
    'web-page': renderWebPageResult,
    'web-pages-list': renderWebPagesListResult,
    xml: renderCodeResult({ language: 'xml' }),
    html: renderCodeResult({ language: 'html' }),
    code: renderCodeResult({ language: 'javascript' }),
  };

  return contentTypeRenderersCache;
};

const DEFAULT_CONFIG: ToolConfig = {
  name: 'Action',
  icon: Location01Icon,
  category: 'intelligence',
  cardType: 'inline',
  runningText: 'Working...',
};

export function getToolConfig(toolName?: string): ToolConfig {
  const toolDisplayConfig = getToolDisplayConfig();
  const explicitConfig = toolName ? toolDisplayConfig[toolName] : undefined;

  if (explicitConfig) {
    return explicitConfig;
  }

  if (toolName) {
    const normalizedKey = normalizeToolName(toolName);
    const normalizedConfig = toolDisplayConfig[normalizedKey];

    if (normalizedConfig) {
      return normalizedConfig;
    }
  }

  if (toolName && isMcpToolName(toolName)) {
    return {
      name: getMcpToolDisplayName(toolName),
      icon: CableIcon,
      category: 'intelligence',
      cardType: 'inline',
      runningText: 'Using MCP...',
      renderOutput: renderMcpBloodhoundResult,
    };
  }

  return toolDisplayConfig[toolName || ''] ?? DEFAULT_CONFIG;
}

export function getContentTypeRenderer(
  contentType: string,
): ((ctx: OutputRenderContext) => React.ReactNode) | undefined {
  return getContentTypeRenderers()[contentType];
}

export const defaultEditModalRenderer = renderDefaultEditModal;
export const defaultEditContentRenderer = renderDefaultEditContent;
export const defaultContentRenderer = renderMarkdownContent;
export const defaultPayloadFormatter = formatDefaultPayload;
